/**
 * Carrier Messages Component - Real-Time Communication for Carriers in The Hive
 * Features:
 * - WebSocket-based instant messaging with admin
 * - Pusher push notifications
 * - Receive and respond to load offers
 * - Document signing with type-to-sign
 * - File sharing (images, PDFs)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MessageCircle, Send, Image, FileText, Package, Search,
  Clock, CheckCircle, X, AlertCircle, Bell, Loader2,
  Circle, Check, CheckCheck, Paperclip, ThumbsUp, ThumbsDown,
  PenTool, FileSignature, Download
} from 'lucide-react';
import { subscribeToCarrierNotifications, unsubscribeFromChannel, NotificationEvents } from '../../services/pusherService';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Typing indicator component
const TypingIndicator = ({ tierColor }) => (
  <div className="flex items-center gap-1 px-3 py-2">
    <div className="flex gap-1">
      <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: tierColor, animationDelay: '0ms' }}></span>
      <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: tierColor, animationDelay: '150ms' }}></span>
      <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: tierColor, animationDelay: '300ms' }}></span>
    </div>
    <span className="text-white/50 text-xs ml-2">typing...</span>
  </div>
);

const CarrierMessages = ({ carrierId, carrierName, tierColor = '#D4AF37' }) => {
  const [activeView, setActiveView] = useState('messages'); // messages, load-offers, signatures
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadOffers, setLoadOffers] = useState([]);
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [showSignModal, setShowSignModal] = useState(null);
  const [ws, setWs] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false); // Track if admin is typing
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  // WebSocket connection
  useEffect(() => {
    if (!carrierId) return;

    // Connect WebSocket
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/api/messaging/ws/${carrierId}/carrier`;
    
    const websocket = new WebSocket(wsUrl);
    
    websocket.onopen = () => {
      console.log('Carrier WebSocket connected');
      setWs(websocket);
    };
    
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWebSocketMessage(data);
    };
    
    websocket.onclose = () => {
      console.log('Carrier WebSocket disconnected');
    };
    
    websocket.onerror = (error) => {
      console.error('Carrier WebSocket error:', error);
    };

    return () => {
      if (websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
    };
  }, [carrierId]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'new_message':
        if (selectedConversation?.id === data.message.conversation_id) {
          setMessages(prev => [...prev, data.message]);
          // Clear typing indicator when message received
          setIsTyping(false);
        }
        fetchConversations();
        fetchUnreadCount();
        // Browser notification
        if (Notification.permission === 'granted') {
          new Notification('New Message from Queen B', {
            body: data.message.content,
            icon: '/logo192.png'
          });
        }
        break;
      case 'load_offer':
        setLoadOffers(prev => [data.offer, ...prev]);
        // Notification
        if (Notification.permission === 'granted') {
          new Notification('New Load Offer', {
            body: `${data.offer.origin || ''} → ${data.offer.destination || ''}`,
            icon: '/logo192.png'
          });
        }
        break;
      case 'signature_request':
        setSignatureRequests(prev => [data.request, ...prev]);
        if (Notification.permission === 'granted') {
          new Notification('Document Requires Signature', {
            body: data.request.document_name,
            icon: '/logo192.png'
          });
        }
        break;
      case 'typing':
        // Handle typing indicator from admin
        if (selectedConversation?.id === data.conversation_id) {
          setIsTyping(true);
          // Auto-clear typing after 3 seconds
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            setIsTyping(false);
          }, 3000);
        }
        break;
      case 'read_receipt':
        // Update read status for messages
        if (data.message_ids && data.message_ids.length > 0) {
          setMessages(prev => prev.map(msg => 
            data.message_ids.includes(msg.id) ? { ...msg, read: true } : msg
          ));
        }
        break;
      default:
        break;
    }
  }, [selectedConversation]);

  // Fetch data on mount
  useEffect(() => {
    if (!carrierId) return;
    
    fetchConversations();
    fetchLoadOffers();
    fetchSignatureRequests();
    fetchUnreadCount();
    
    // Request notification permission (check if API exists first)
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    // Subscribe to Pusher notifications
    let pusherChannel = null;
    const setupPusher = async () => {
      pusherChannel = await subscribeToCarrierNotifications(carrierId, {
        [NotificationEvents.NEW_MESSAGE]: (data) => {
          fetchConversations();
          fetchUnreadCount();
        },
        [NotificationEvents.NEW_LOAD_OFFER]: (data) => {
          fetchLoadOffers();
        },
        [NotificationEvents.SIGNATURE_REQUEST]: (data) => {
          fetchSignatureRequests();
        }
      });
    };
    setupPusher();
    
    setLoading(false);
    
    // Cleanup on unmount
    return () => {
      if (pusherChannel) {
        unsubscribeFromChannel(`carrier-${carrierId}`);
      }
    };
  }, [carrierId]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/conversations/${carrierId}?user_type=carrier`);
      const data = await res.json();
      setConversations(data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/conversations/${conversationId}/messages`);
      const data = await res.json();
      setMessages(data);
      
      // Mark as read
      const unreadIds = data.filter(m => !m.read && m.recipient_id === carrierId).map(m => m.id);
      if (unreadIds.length > 0 && ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'read', message_ids: unreadIds }));
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchLoadOffers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/load-offers/carrier/${carrierId}`);
      const data = await res.json();
      setLoadOffers(data);
    } catch (error) {
      console.error('Failed to fetch load offers:', error);
    }
  };

  const fetchSignatureRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/signature-requests/carrier/${carrierId}`);
      const data = await res.json();
      setSignatureRequests(data);
    } catch (error) {
      console.error('Failed to fetch signature requests:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/unread-count/${carrierId}`);
      const data = await res.json();
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setIsTyping(false);
    fetchMessages(conversation.id);
  };

  // Send typing indicator to WebSocket
  const sendTypingIndicator = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedConversation) return;
    
    // Throttle typing indicators (send max once per second)
    const now = Date.now();
    if (now - lastTypingSentRef.current < 1000) return;
    lastTypingSentRef.current = now;
    
    ws.send(JSON.stringify({
      type: 'typing',
      conversation_id: selectedConversation.id,
      recipient_id: 'admin'
    }));
  }, [ws, selectedConversation]);

  // Handle message input change with typing indicator
  const handleMessageChange = (e) => {
    setNewMessage(e.target.value);
    if (e.target.value.trim()) {
      sendTypingIndicator();
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const res = await fetch(`${API_URL}/api/messaging/messages?sender_id=${carrierId}&sender_name=${encodeURIComponent(carrierName)}&sender_type=carrier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: selectedConversation.id,
          recipient_id: 'admin',
          content: newMessage,
          message_type: 'text'
        })
      });

      if (res.ok) {
        const message = await res.json();
        setMessages(prev => [...prev, message]);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const startConversationWithAdmin = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/messages?sender_id=${carrierId}&sender_name=${encodeURIComponent(carrierName)}&sender_type=carrier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_id: 'admin',
          content: 'Hello! I need assistance.',
          message_type: 'text'
        })
      });

      if (res.ok) {
        fetchConversations();
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
    }
  };

  const respondToLoadOffer = async (offerId, response) => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/load-offers/${offerId}/respond?response=${response}&carrier_id=${carrierId}`, {
        method: 'POST'
      });

      if (res.ok) {
        fetchLoadOffers();
      }
    } catch (error) {
      console.error('Failed to respond to load offer:', error);
    }
  };

  const signDocument = async (requestId, signatureName) => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/signature-requests/${requestId}/sign?carrier_id=${carrierId}&signature_name=${encodeURIComponent(signatureName)}&signature_style=cursive`, {
        method: 'POST'
      });

      if (res.ok) {
        setShowSignModal(null);
        fetchSignatureRequests();
      }
    } catch (error) {
      console.error('Failed to sign document:', error);
    }
  };

  // Count pending items
  const pendingLoadOffers = loadOffers.filter(o => o.status === 'pending').length;
  const pendingSignatures = signatureRequests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: tierColor }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="carrier-messages">
      {/* Tab Navigation */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setActiveView('messages')}
          className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
            activeView === 'messages' 
              ? 'text-black' 
              : 'bg-[#121212] border border-white/10 text-white hover:bg-white/5'
          }`}
          style={activeView === 'messages' ? { backgroundColor: tierColor } : {}}
          data-testid="carrier-messages-tab"
        >
          <MessageCircle className="w-4 h-4" />
          Messages
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">{unreadCount}</span>
          )}
        </button>
        <button
          onClick={() => setActiveView('load-offers')}
          className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
            activeView === 'load-offers' 
              ? 'text-black' 
              : 'bg-[#121212] border border-white/10 text-white hover:bg-white/5'
          }`}
          style={activeView === 'load-offers' ? { backgroundColor: tierColor } : {}}
          data-testid="carrier-load-offers-tab"
        >
          <Package className="w-4 h-4" />
          Load Offers
          {pendingLoadOffers > 0 && (
            <span className="px-2 py-0.5 text-xs bg-yellow-500 text-black rounded-full">{pendingLoadOffers}</span>
          )}
        </button>
        <button
          onClick={() => setActiveView('signatures')}
          className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
            activeView === 'signatures' 
              ? 'text-black' 
              : 'bg-[#121212] border border-white/10 text-white hover:bg-white/5'
          }`}
          style={activeView === 'signatures' ? { backgroundColor: tierColor } : {}}
          data-testid="carrier-signatures-tab"
        >
          <PenTool className="w-4 h-4" />
          Signatures
          {pendingSignatures > 0 && (
            <span className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">{pendingSignatures}</span>
          )}
        </button>
      </div>

      {/* Messages View */}
      {activeView === 'messages' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[500px]">
          {/* Conversation List */}
          <div className="lg:col-span-1 bg-[#121212] border border-white/10 flex flex-col">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-bold text-sm">Conversations</h3>
              {conversations.length === 0 && (
                <button
                  onClick={startConversationWithAdmin}
                  className="px-3 py-1 text-xs font-medium"
                  style={{ backgroundColor: tierColor, color: 'black' }}
                >
                  Contact Admin
                </button>
              )}
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageCircle className="w-10 h-10 text-white/20 mx-auto mb-2" />
                  <p className="text-white/50 text-sm">No messages yet</p>
                  <p className="text-white/30 text-xs mt-1">Click "Contact Admin" to start</p>
                </div>
              ) : (
                conversations.map(conv => {
                  const admin = conv.participants.find(p => p.type === 'admin');
                  return (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      className={`w-full p-4 text-left border-b border-white/5 hover:bg-white/5 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-white/10' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-10 h-10 flex items-center justify-center font-bold"
                          style={{ backgroundColor: `${tierColor}20`, color: tierColor }}
                        >
                          QB
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium">{admin?.name || 'Queen B Admin'}</p>
                          <p className="text-white/50 text-sm truncate">
                            {conv.last_message?.content || 'No messages'}
                          </p>
                        </div>
                        {conv.unread_count > 0 && (
                          <span 
                            className="px-2 py-0.5 text-xs text-black rounded-full"
                            style={{ backgroundColor: tierColor }}
                          >
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="lg:col-span-2 bg-[#121212] border border-white/10 flex flex-col">
            {selectedConversation ? (
              <>
                {/* Header */}
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 flex items-center justify-center font-bold"
                      style={{ backgroundColor: `${tierColor}20`, color: tierColor }}
                    >
                      QB
                    </div>
                    <div>
                      <p className="text-white font-medium">Queen B Admin</p>
                      <p className="text-white/40 text-xs flex items-center gap-1">
                        <Circle className="w-2 h-2 fill-green-400 text-green-400" />
                        Online
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.sender_type === 'carrier' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%]`}>
                        <div 
                          className={`p-3 ${
                            msg.sender_type === 'carrier' 
                              ? 'text-black' 
                              : 'bg-[#0A0A0A] border border-white/10 text-white'
                          }`}
                          style={msg.sender_type === 'carrier' ? { backgroundColor: tierColor } : {}}
                        >
                          {msg.message_type === 'image' && msg.attachment_url && (
                            <img src={msg.attachment_url} alt="Shared" className="max-w-full mb-2 rounded" />
                          )}
                          {msg.message_type === 'pdf' && msg.attachment_url && (
                            <a 
                              href={msg.attachment_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-black/10 mb-2"
                            >
                              <FileText className="w-5 h-5" />
                              <span className="text-sm">{msg.attachment_name || 'Document.pdf'}</span>
                            </a>
                          )}
                          <p className="text-sm">{msg.content}</p>
                        </div>
                        <div className={`flex items-center gap-1 text-xs mt-1 ${msg.sender_type === 'carrier' ? 'justify-end' : 'justify-start'} text-white/40`}>
                          <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          {msg.sender_type === 'carrier' && (
                            <span className="flex items-center" title={msg.read ? 'Read' : 'Delivered'}>
                              {msg.read ? (
                                <CheckCheck className="w-3.5 h-3.5" style={{ color: tierColor }} />
                              ) : (
                                <Check className="w-3.5 h-3.5 text-white/40" />
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Typing Indicator */}
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg">
                        <TypingIndicator tierColor={tierColor} />
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <button className="p-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors">
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={handleMessageChange}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      className="flex-1 bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white placeholder:text-white/30 focus:outline-none"
                      style={{ '--tw-ring-color': tierColor }}
                      data-testid="carrier-message-input"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      className="p-2 text-black disabled:opacity-50"
                      style={{ backgroundColor: tierColor }}
                      data-testid="carrier-send-btn"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-white/10 mx-auto mb-4" />
                  <p className="text-white/50">Select a conversation</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Load Offers View */}
      {activeView === 'load-offers' && (
        <div className="space-y-4">
          {/* Pending Offers */}
          {loadOffers.filter(o => o.status === 'pending').length > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-4">
              <h3 className="text-yellow-400 font-bold mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Pending Offers - Action Required
              </h3>
              <div className="space-y-3">
                {loadOffers.filter(o => o.status === 'pending').map(offer => (
                  <div key={offer.id} className="bg-[#121212] border border-white/10 p-4">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1">
                        <p className="text-white font-medium mb-2">{offer.load_details}</p>
                        {(offer.origin || offer.destination) && (
                          <p className="text-white/70">
                            📍 {offer.origin} → {offer.destination}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2">
                          {offer.rate && (
                            <span className="text-[#22C55E] font-bold text-lg">${offer.rate.toLocaleString()}</span>
                          )}
                          {offer.pickup_date && (
                            <span className="text-white/50 text-sm">Pickup: {offer.pickup_date}</span>
                          )}
                        </div>
                      </div>
                      {offer.screenshot_url && (
                        <img 
                          src={offer.screenshot_url} 
                          alt="Load" 
                          className="w-32 h-32 object-cover border border-white/10"
                        />
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => respondToLoadOffer(offer.id, 'accepted')}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-500 text-white font-medium hover:bg-green-600 transition-colors"
                        data-testid={`accept-offer-${offer.id}`}
                      >
                        <ThumbsUp className="w-5 h-5" />
                        Accept
                      </button>
                      <button
                        onClick={() => respondToLoadOffer(offer.id, 'declined')}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-500 text-white font-medium hover:bg-red-600 transition-colors"
                        data-testid={`decline-offer-${offer.id}`}
                      >
                        <ThumbsDown className="w-5 h-5" />
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History */}
          <div className="bg-[#121212] border border-white/10">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-white font-bold">Load Offer History</h3>
            </div>
            {loadOffers.filter(o => o.status !== 'pending').length === 0 ? (
              <div className="p-8 text-center">
                <Package className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No load offer history</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {loadOffers.filter(o => o.status !== 'pending').map(offer => (
                  <div key={offer.id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white">{offer.load_details}</p>
                      <span className={`px-2 py-1 text-xs ${
                        offer.status === 'accepted' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {offer.status}
                      </span>
                    </div>
                    {(offer.origin || offer.destination) && (
                      <p className="text-white/50 text-sm">{offer.origin} → {offer.destination}</p>
                    )}
                    <p className="text-white/40 text-xs mt-1">
                      Responded: {new Date(offer.responded_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signatures View */}
      {activeView === 'signatures' && (
        <div className="space-y-4">
          {/* Pending Signatures */}
          {signatureRequests.filter(r => r.status === 'pending').length > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/30 p-4">
              <h3 className="text-orange-400 font-bold mb-3 flex items-center gap-2">
                <PenTool className="w-5 h-5" />
                Documents Awaiting Signature
              </h3>
              <div className="space-y-3">
                {signatureRequests.filter(r => r.status === 'pending').map(req => (
                  <div key={req.id} className="bg-[#121212] border border-white/10 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <FileSignature className="w-8 h-8 text-orange-400" />
                        <div>
                          <p className="text-white font-medium">{req.document_name}</p>
                          <p className="text-white/50 text-sm">
                            Received: {new Date(req.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <a
                        href={req.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-white/10 text-white hover:bg-white/20 transition-colors"
                      >
                        <Download className="w-5 h-5" />
                        View Document
                      </a>
                      <button
                        onClick={() => setShowSignModal(req)}
                        className="flex-1 flex items-center justify-center gap-2 py-3 text-black font-medium"
                        style={{ backgroundColor: tierColor }}
                        data-testid={`sign-doc-${req.id}`}
                      >
                        <PenTool className="w-5 h-5" />
                        Sign Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Signed Documents */}
          <div className="bg-[#121212] border border-white/10">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-white font-bold">Signed Documents</h3>
            </div>
            {signatureRequests.filter(r => r.status === 'signed').length === 0 ? (
              <div className="p-8 text-center">
                <FileSignature className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No signed documents</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {signatureRequests.filter(r => r.status === 'signed').map(req => (
                  <div key={req.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-400" />
                      <div>
                        <p className="text-white">{req.document_name}</p>
                        <p className="text-white/50 text-sm">
                          Signed: {new Date(req.signed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <a
                      href={req.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-white/50 hover:text-white transition-colors"
                    >
                      <Download className="w-5 h-5" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {showSignModal && (
        <SignatureModal
          request={showSignModal}
          carrierName={carrierName}
          tierColor={tierColor}
          onClose={() => setShowSignModal(null)}
          onSign={(name) => signDocument(showSignModal.id, name)}
        />
      )}
    </div>
  );
};

// Signature Modal with Type-to-Sign
const SignatureModal = ({ request, carrierName, tierColor, onClose, onSign }) => {
  const [signatureName, setSignatureName] = useState(carrierName || '');
  const [agreed, setAgreed] = useState(false);

  const handleSign = () => {
    if (!signatureName.trim() || !agreed) return;
    onSign(signatureName);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-white/10 w-full max-w-md">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-white font-bold">Sign Document</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10">
            <X className="w-5 h-5 text-white/50" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {/* Document Info */}
          <div className="bg-[#0A0A0A] border border-white/10 p-4">
            <p className="text-white font-medium">{request.document_name}</p>
            <a
              href={request.document_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm hover:underline"
              style={{ color: tierColor }}
            >
              View Document →
            </a>
          </div>

          {/* Type Signature */}
          <div>
            <label className="text-white/40 text-xs uppercase mb-2 block">Type Your Full Name</label>
            <input
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Your full legal name"
              className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none"
              data-testid="signature-name-input"
            />
          </div>

          {/* Signature Preview */}
          <div>
            <label className="text-white/40 text-xs uppercase mb-2 block">Signature Preview</label>
            <div className="bg-white p-6 text-center">
              <p 
                className="text-3xl text-gray-800"
                style={{ fontFamily: 'cursive, Georgia, serif' }}
              >
                {signatureName || 'Your Signature'}
              </p>
            </div>
          </div>

          {/* Agreement */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1"
            />
            <span className="text-white/70 text-sm">
              I agree that this electronic signature is the legal equivalent of my manual signature on this document.
            </span>
          </label>
        </div>
        <div className="p-4 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white/70 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSign}
            disabled={!signatureName.trim() || !agreed}
            className="px-6 py-2 text-black font-medium disabled:opacity-50"
            style={{ backgroundColor: tierColor }}
            data-testid="confirm-signature-btn"
          >
            Sign Document
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarrierMessages;
